import React, { useEffect, useRef, useState } from 'react';
import { DataService } from '../../services/data-service';
import { broadcastProjectsChanged } from '../../services/project-changes-channel';
import ClientMatchDialog, { type ClientMatchSeed } from './ClientMatchDialog';
import { ProjectClientContext, ProjectClientControl } from './project-client-context';
import { clientMatchHintsPayload, hasClientMatchEvidence, hasClientMatchSignal, projectClientMatchHints, projectClientMatchInput } from './project-client-match';
import './client-match.css';

interface Props {
    project: any;
    tenantId?: string | null;
    formData: any;
    busy?: boolean;
    active?: boolean;
    /** A Quote Run is driving jobs in this tab. */
    quoteRunActive?: boolean;
    /** Changes on every project open (App's load generation). */
    openKey?: number | string | null;
    children?: React.ReactNode;
    /** propertyId is left out when the answer did not say, so a known site is kept. */
    onLinked: (projectId: string, link: { companyId: string; propertyId?: string | null }) => void;
    /** Opens Clients with Tidy up (the dialog's unmanaged-building note). */
    onOpenTidy?: () => void;
}

type ResolveAsked = { email: string; name: string; pdfFirmName: string; plan: string };
type ResolveAnswer = {
    state?: 'linked' | 'auto-linked' | 'deferred' | 'ask' | string;
    company?: { id: string; name?: string } | null;
    evidence?: string | null;
    propertyId?: string | null;
    property_id?: string | null;
} & ClientMatchSeed;

// The resolve evidence that means the requester email named the firm (D2).
const EMAIL_EVIDENCE = new Set(['contact-email', 'building-contact-email', 'domain']);

// True in every run state: while the run reads files, between its jobs, and
// over a job on screen that is not in the run at all.
const QUOTE_RUN_REASON = 'A Quote Run is running in this tab. Choose the client when it finishes.';

/**
 * The client of a job (SPEC 2026-10-08 D2, D3).
 *
 * Each open of a job (a new openKey) whose list row has no client asks the
 * server once (POST /projects/:id/crm-link/resolve). The server, not the Run
 * Sheet list row, knows whether the job is linked: the row can be missing or
 * stale (C2). It answers linked (the row is corrected, no prompt), auto-linked
 * (the requester email named exactly one firm; the header says "Matched from
 * <email>" with Change), deferred ("Match later" was pressed for this email:
 * nothing) or ask (the prompt opens with the server's suggestions). Offline or
 * on any error the job opens with no prompt: matching never blocks a quote.
 *
 * Closing the prompt stores "Match later" on the job with the email it
 * answered, so the next open does not ask again unless that email changes.
 * Closing never blocks autosave or changes quote content.
 *
 * While a Quote Run runs in this tab nothing is asked: the prompt's backdrop
 * would cover the Quote Run rail and manager. A job the run opened is marked
 * as asked, so the prompt does not pop when the run ends; the operator's own
 * next open of that job (a new openKey) asks, like any other open.
 */
export default function ProjectClientLink({ project, tenantId, formData, busy = false, active = true, quoteRunActive = false, openKey = null, children, onLinked, onOpenTidy }: Props) {
    const listCompanyId = project?.companyId ?? project?.company_id ?? null;
    const [company, setCompany] = useState<any>(null);
    const [companyScope, setCompanyScope] = useState('');
    const [open, setOpen] = useState(false);
    // The link this tab learned from the server (resolve or a choice), for a
    // job whose list row is missing: the header must not say "Match client".
    const [learned, setLearned] = useState<{ scope: string; companyId: string; matchedFrom: string | null } | null>(null);
    // The server's 'ask' answer, kept with the open and the request it
    // answered. It seeds only the prompt that answer opened: a later open, a
    // changed request or a closed prompt searches afresh, so the dialog never
    // shows matches the CRM or the requester email has moved past.
    const [seed, setSeed] = useState<{ scope: string; openMark: string; asked: ResolveAsked; value: ClientMatchSeed } | null>(null);
    const prompted = useRef(false);
    const promptedMark = useRef('');
    // The scope and open the run held back, so the operator's own open asks.
    const heldByRun = useRef<string | null>(null);
    const mounted = useRef(true);
    const resolveSeq = useRef(0);
    const projectId = project?.id;
    const scopeKey = `${tenantId || ''}:${projectId || ''}`;
    const currentScope = useRef(scopeKey);
    currentScope.current = scopeKey;
    const live = useRef({ active, quoteRunActive });
    live.current = { active, quoteRunActive };
    const input = projectClientMatchInput(formData);
    // E5 (2026-10-13): the people and the firm the RFQ's text names.
    const hints = projectClientMatchHints(formData);
    const hintsPayload = clientMatchHintsPayload(hints);
    const hintsKey = JSON.stringify(hintsPayload);
    const learnedHere = learned && learned.scope === scopeKey ? learned : null;
    const companyId = listCompanyId || learnedHere?.companyId || null;
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => { prompted.current = false; heldByRun.current = null; resolveSeq.current += 1; setOpen(false); }, [scopeKey]);
    useEffect(() => { if (!active) setOpen(false); }, [active]);
    const openMark = `${scopeKey}#${openKey ?? ''}`;
    // Each open of a job asks once. A run holding this open keeps it asked.
    if (promptedMark.current !== openMark) {
        promptedMark.current = openMark;
        if (heldByRun.current !== openMark) prompted.current = false;
    }
    useEffect(() => {
        if (quoteRunActive) {
            prompted.current = true;
            heldByRun.current = openMark;
            setOpen(false);
        } else if (heldByRun.current && heldByRun.current !== openMark) {
            heldByRun.current = null;
            prompted.current = false;
        }
    }, [quoteRunActive, openMark]);
    // The client the server named in its answer, shown until the cache read lands.
    const knownCompany = useRef<{ scope: string; company: { id: string; name?: string } } | null>(null);
    useEffect(() => {
        let cancelled = false;
        const known = knownCompany.current;
        setCompany(known && known.scope === scopeKey && known.company.id === companyId ? known.company : null);
        setCompanyScope(scopeKey);
        if (companyId) DataService.companies.getById(companyId).then((row: { id: string; name?: string } | null) => { if (!cancelled && row) setCompany(row); }).catch(() => {});
        return () => { cancelled = true; };
    }, [companyId, scopeKey]);

    const applyLink = (linkedProjectId: string, scope: string, answer: ResolveAnswer, matchedFrom: string | null) => {
        const linkedId = answer.company?.id;
        if (!linkedId) return;
        // null means the job has no site on the server, so it is passed on to clear it.
        const propertyId = answer.propertyId !== undefined ? answer.propertyId : answer.property_id;
        if (answer.company?.name) knownCompany.current = { scope, company: answer.company as { id: string; name?: string } };
        onLinked(linkedProjectId, propertyId === undefined ? { companyId: linkedId } : { companyId: linkedId, propertyId });
        setLearned({ scope, companyId: linkedId, matchedFrom });
    };

    useEffect(() => {
        if (!active || !projectId || busy || quoteRunActive || listCompanyId || prompted.current || !hasClientMatchEvidence(input, hints)) return;
        prompted.current = true;
        setSeed(null);
        const seq = ++resolveSeq.current;
        const scope = scopeKey;
        const mark = openMark;
        const asked: ResolveAsked = { email: input.email, name: input.firmName, pdfFirmName: input.pdfFirmName, plan: input.plan };
        const promptOnAsk = hasClientMatchSignal(input);
        const current = () => mounted.current && currentScope.current === scope && resolveSeq.current === seq;
        Promise.resolve()
            .then(() => (DataService.projects as any).resolveCrmLink(projectId, { ...asked, ...hintsPayload }))
            .then((answer: ResolveAnswer | null) => {
                if (!answer || typeof answer !== 'object') return;
                if (answer.state === 'auto-linked' && answer.company?.id && currentScope.current === scope) {
                    // The server wrote the link: other tabs refresh their lists.
                    broadcastProjectsChanged({ kind: 'crm-linked', projectIds: [projectId] });
                }
                if (!current()) return;
                if (answer.state === 'linked' || answer.state === 'auto-linked') {
                    // "Matched from" names the email, as before, else the first
                    // RFQ signal the server linked on ("Strata plan SP 2068").
                    const firstSignal = Array.isArray(answer.signals) ? answer.signals.find(signal => signal?.label)?.label || null : null;
                    const matchedFrom = answer.state !== 'auto-linked' ? null
                        : EMAIL_EVIDENCE.has(String(answer.evidence)) ? (asked.email || null)
                            : firstSignal;
                    applyLink(projectId, scope, answer, matchedFrom);
                } else if (answer.state === 'ask') {
                    setSeed({ scope, openMark: mark, asked, value: answer });
                    // Only a job that prompted before E5 (an email or a firm
                    // name) opens the prompt by itself. A job known only by its
                    // plan or the people it names is linked when the server can,
                    // and otherwise waits for the header's Match client.
                    if (promptOnAsk && live.current.active && !live.current.quoteRunActive) setOpen(true);
                }
                // 'deferred': "Match later" stands for this email. Nothing to show.
            })
            // Offline or refused: no prompt on this open. The header control still offers it.
            .catch(() => {});
    }, [active, projectId, scopeKey, openMark, busy, quoteRunActive, listCompanyId, input.email, input.firmName, input.pdfFirmName, input.plan, hintsKey]);

    const matchLater = () => {
        setOpen(false);
        setSeed(null);
        if (!projectId || companyId) return;
        // Stored on the job with the email it answered (D3). A failure only
        // means the next open asks again, so it is not reported.
        Promise.resolve()
            .then(() => (DataService.projects as any).deferCrmLink(projectId, input.email || null))
            .catch(() => {});
    };

    const seedHere = seed && seed.scope === scopeKey && seed.openMark === openMark
        && seed.asked.email === input.email && seed.asked.name === input.firmName
        && seed.asked.pdfFirmName === input.pdfFirmName && seed.asked.plan === input.plan
        ? seed.value : null;
    // A strata job's search starts from the firm the requester email names (D5).
    // Any other job searches what its request names, as before (I5); the email
    // firm is still listed first.
    const draftName = (input.strataJob && seedHere?.suggestedFirm?.name) || input.firmName;
    return <ProjectClientContext.Provider value={active && projectId ? {
        name: (companyScope === scopeKey && company?.name) || 'Client linked',
        linked: !!companyId,
        busy: busy || quoteRunActive,
        busyReason: quoteRunActive ? QUOTE_RUN_REASON : null,
        matchedFrom: learnedHere?.matchedFrom ?? null,
        open: () => { if (!quoteRunActive) setOpen(true); },
    } : null}>
        {children === undefined ? <ProjectClientControl /> : children}
        {open && <ClientMatchDialog
            draft={{ name: draftName, email: input.email, phone: input.phone }}
            context={project.title || formData?.address}
            seed={seedHere}
            source={input.strataJob ? { email: input.email, pdfFirmName: seedHere?.pdfFirmName || input.pdfFirmName, plan: input.plan } : null}
            planNumber={input.plan}
            offerOwnersCorporation={false}
            firmsOnly
            onOpenTidy={onOpenTidy ? () => { setOpen(false); setSeed(null); onOpenTidy(); } : undefined}
            onClose={matchLater}
            onChoose={async (client) => {
                if (!mounted.current || currentScope.current !== scopeKey) throw new Error('This request is no longer open. Reopen it to choose its client.');
                // The operator's choice teaches the RFQ layout (E5 signal 5).
                const result = await DataService.projects.setCrmLink(projectId, {
                    companyId: client.id,
                    ...(hints.formFingerprint ? { formFingerprint: hints.formFingerprint } : {}),
                });
                const link = { companyId: result.companyId ?? result.company_id, propertyId: result.propertyId ?? result.property_id ?? null };
                if (currentScope.current !== scopeKey) return;
                // The narrow API/cache writer persists only CRM foreign keys. No form,
                // scope, quote or media payload can be overwritten by this decision.
                broadcastProjectsChanged({ kind: 'crm-linked', projectIds: [projectId] });
                if (!mounted.current) return;
                if (link.companyId === client.id) knownCompany.current = { scope: scopeKey, company: client };
                onLinked(projectId, link);
                setLearned({ scope: scopeKey, companyId: link.companyId, matchedFrom: null });
                setCompany(client);
                setCompanyScope(scopeKey);
                setOpen(false);
                setSeed(null);
            }} />}
    </ProjectClientContext.Provider>;
}
